/**
 * @tt/lobby/cosmetic-source-text — Phase F4.
 *
 * Best-effort human-readable unlock hint for a `CosmeticSource`. Used by
 * the locker to surface the SPECIFIC unlock path on locked tiles instead
 * of the previous generic "Unlock through play" string.
 *
 * Lookups:
 *   - Achievement labels resolve from `@tt/achievements`'s ACHIEVEMENTS_V2.
 *   - Bundle names resolve from BUNDLES in `./cosmetics-catalog`.
 *   - Both fall back to the raw id if the catalog has no matching entry —
 *     keeps the locker rendering a sane string even mid-migration.
 *
 * Kept in its own module (rather than appended to `cosmetic-resolver.ts`)
 * so the achievements dependency is opt-in: lambdas that only need the
 * inventory bridge (lambda/season, lambda/shop) keep their existing
 * import surface.
 */
import { ACHIEVEMENTS_V2 } from '@tt/achievements';
import { BUNDLES, type CosmeticSource } from './cosmetics-catalog';

/** Human-readable acquisition hint for a cosmetic source. The strings
 *  are intentionally short (≤ ~40 chars) so they fit the locker's
 *  locked-tile hint slot without wrapping awkwardly. */
export function unlockHintText(source: CosmeticSource): string {
  switch (source.kind) {
    case 'default':
      return 'Default — should already be owned';
    case 'level':
      return `Reach account level ${source.level}`;
    case 'achievement':
      return `Earn the "${achievementLabelFor(source.id)}" achievement`;
    case 'season-free':
      return `Season ${source.season} Pass · tier ${source.tier}`;
    case 'season-premium':
      return `Season ${source.season} Premium Pass · tier ${source.tier}`;
    case 'lantern-shop':
      return `Lantern Shop · ${source.price.toLocaleString()} 🏮`;
    case 'embers-shop':
      return `Embers Shop · ${source.price.toLocaleString()} embers`;
    case 'bundle':
      return `Part of the "${bundleNameFor(source.bundleId)}" bundle`;
  }
}

function achievementLabelFor(id: string): string {
  const a = ACHIEVEMENTS_V2.find((x) => x.code === id);
  return a?.label ?? id;
}

function bundleNameFor(id: string): string {
  const b = BUNDLES.find((x) => x.id === id);
  return b?.name ?? id;
}
